/**
 * Thin fetch wrapper with JWT injection + error normalization.
 *
 * In production the Vue app is served by the FastAPI image at the same origin
 * as /v1/*, so the API base is the empty string (relative URLs).
 * In `npm run dev` Vite's proxy forwards /v1 to http://localhost:8000.
 * VITE_API_MODE=mock can short-circuit to mock fixtures (see services/api.ts).
 */

const API_BASE = ''; // same-origin in dev (Vite proxy) and prod (FastAPI mount)

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.name = 'ApiError';
  }
}

const TOKEN_KEY = 'rounds_jwt_v1';

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  formBody?: Record<string, string>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** When true, omit the Authorization header even if a token is stored. */
  anonymous?: boolean;
  /**
   * When true, a 401 response does NOT clear the stored token or redirect to
   * /login — the caller owns the failure. Used by the token-validation probe
   * (auth.me on app boot): a stale in-flight /me validating an expired token
   * must not tear down a session a concurrent login() just established, nor
   * hard-redirect the page mid-login. Without this the post-login redirect
   * gets clobbered and login appears to need several attempts.
   */
  noAuthHandling?: boolean;
  /**
   * Per-request timeout in ms. Defaults to DEFAULT_TIMEOUT_MS. Pass 0 to
   * disable (e.g. long-running exports). On timeout the request is aborted
   * and the call rejects with ApiError(0, {code:'TIMEOUT'}).
   */
  timeoutMs?: number;
}

// Time-box every request. Without this, a hung backend — a Railway api
// restart / cold-start window, a dropped connection — leaves fetch pending
// forever, freezing any view that awaits it on a spinner until the user
// manually refreshes (the "login lands on a blank dashboard" report). A
// timeout converts an infinite hang into a normal rejection the caller's
// catch can handle (e.g. DashboardView renders empty instead of spinning).
const DEFAULT_TIMEOUT_MS = 20000;

/**
 * If `e` is an opaque network-layer fetch failure ("Failed to fetch" /
 * "NetworkError" / "Load failed"), return an actionable hint disambiguating a
 * mixed-content block from a CORS/DNS/offline error. Otherwise null.
 *
 * Shared by http() AND the direct-to-GCS file PUT legs (UploadView,
 * AddFileModal) which bypass http() — the PUT is exactly where an upload CORS
 * failure lands, so without this the most likely failure point still surfaced a
 * bare "Failed to fetch". Callers prepend their own context ("Could not reach
 * the API/storage: …").
 */
export function networkBlockedHint(e: unknown): string | null {
  const msg = e instanceof Error ? e.message : String(e ?? '');
  if (!/failed to fetch|networkerror|load failed/i.test(msg)) return null;
  const mixed = typeof window !== 'undefined' && window.location.protocol === 'http:';
  return mixed
    ? 'the page is not served over HTTPS — the browser blocked the request (mixed content). Open the site via https://'
    : 'likely a CORS, DNS, or offline error — confirm the host is reachable and its CORS allow-lists this origin';
}

export async function http<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { ...opts.headers };
  const token = opts.anonymous ? null : getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.formBody) {
    body = new URLSearchParams(opts.formBody);
    headers['Content-Type'] = 'application/x-www-form-urlencoded';
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers['Content-Type'] = 'application/json';
  }

  // Abort on timeout; also chain the caller's signal so either can cancel.
  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort();
    else opts.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? (body ? 'POST' : 'GET'),
      headers,
      body,
      signal: controller.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      // Caller-initiated abort → propagate as-is; our timeout → typed ApiError.
      if (opts.signal?.aborted) throw e;
      throw new ApiError(0, { code: 'TIMEOUT' }, `Request to ${path} timed out after ${timeoutMs}ms`);
    }
    // Network-level failure (no HTTP response): offline, DNS, connection
    // refused/reset, CORS preflight rejected, or a mixed-content block. The
    // browser collapses all of these into an opaque "Failed to fetch" TypeError,
    // so disambiguate with an actionable hint instead of a bare generic — this
    // is what made the roundstranscript.vin upload show only "Failed to fetch".
    const hint = networkBlockedHint(e);
    if (hint) {
      throw new ApiError(0, { code: 'NETWORK_BLOCKED', hint }, `Could not reach the API: ${hint}.`);
    }
    throw new ApiError(0, { code: 'NETWORK_ERROR' }, e instanceof Error ? e.message : 'Network error');
  } finally {
    if (timer) clearTimeout(timer);
  }

  const isJson = resp.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await resp.json().catch(() => undefined) : await resp.text().catch(() => '');

  // Auto-unwrap MIC §9.1 envelope. Backend wraps every JSON response in
  // {success, data, error, meta} — frontend callers expect the raw `data`
  // payload as before parity-3. Detect the envelope by checking the exact
  // 4-key shape so we don't unwrap a response that legitimately happens to
  // contain a `data` field.
  const unwrapped = unwrapEnvelope(payload);

  if (!resp.ok) {
    if (resp.status === 401 && !opts.anonymous && !opts.noAuthHandling) {
      // JWT expired or revoked. Clear it so the next request prompts login.
      setToken(null);
      // Also redirect the CURRENT page to /login so the user doesn't sit on a
      // broken view watching toasts pile up. Hash-routed app → navigate via
      // location.hash. Guarded so the login form's own 401 (wrong password)
      // doesn't trigger a redirect loop.
      if (!window.location.hash.startsWith('#/login')) {
        window.location.replace('/#/login');
      }
    }
    // Hand the caller the payload that still CARRIES the reason. On an error
    // the envelope's `data` is null by construction (app/middleware/envelope.py
    // builds {success:false, data:null, error:{code,message,...}}), so passing
    // `unwrapped` here threw away the code and message on every backend
    // failure — ApiError.body was null and every consumer fell through to its
    // generic fallback string. A non-admin saving an email template got
    // "403 — Failed to save template" instead of the ADMIN_ONLY reason, and
    // the call sites that already read `body.error.code` (SegmentText.vue:403,
    // AddFileModal.vue:210) could never have matched. `payload` is identical
    // to `unwrapped` for every non-envelope response, so this changes nothing
    // else.
    throw new ApiError(resp.status, payload);
  }
  return unwrapped as T;
}


function unwrapEnvelope(payload: unknown): unknown {
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    'success' in payload &&
    'data' in payload &&
    'error' in payload &&
    'meta' in payload
  ) {
    const env = payload as { success: boolean; data: unknown; error: unknown };
    // On error: surface the error.code/message via ApiError thrown above; but
    // here we still return the envelope's data field (null on error) so the
    // caller can inspect it if it gets through. The non-2xx path already
    // throws ApiError so most callers never see a null-data envelope.
    if (env.success === false && env.error) {
      // Propagate the error shape via the throw path (caller's response was
      // not ok). For 2xx-with-success=false (unusual), return null data.
      return env.data;
    }
    return env.data;
  }
  return payload;
}


/**
 * The human-readable reason behind an ApiError, or a caller-supplied fallback.
 *
 * WHY THIS IS SHARED. Six components grew their own copy of this, and five of
 * them read only FastAPI's raw `detail` shape. EnvelopeMiddleware has not
 * emitted that shape for ordinary handler errors in a long time — it rewrites
 * every error into {success:false, data:null, error:{code,message,...}} — so
 * those five always fell through to their generic fallback string. A non-admin
 * saving an email template was told "403 — Failed to save template" instead of
 * the ADMIN_ONLY reason, which sends someone who merely lacks the admin
 * account looking for a bug.
 *
 * `error.message` is read FIRST because it is what the deployed backend
 * actually returns. The `detail` branch is kept for the handful of responses
 * that bypass the middleware, and costs nothing.
 *
 * @param e        the caught value — anything, so a caller need not narrow it
 * @param fallback what to say when the response carried no reason at all
 */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) {
    return e instanceof Error && e.message ? e.message : fallback;
  }
  const body = e.body as
    | {
        error?: { code?: string; message?: string };
        detail?: { message?: string; code?: string } | string;
      }
    | undefined;

  const enveloped = body?.error;
  if (enveloped && typeof enveloped === 'object' && isProse(enveloped.message)) {
    return enveloped.message!;
  }
  const detail = body?.detail;
  if (detail && typeof detail === 'object' && isProse(detail.message)) return detail.message!;
  if (typeof detail === 'string') return `${e.status} — ${detail}`;

  // http.ts builds its own ApiError for a timeout, a blocked fetch or an
  // offline one, and puts the explanation in `message` with only a bare
  // {code} in the body — see networkBlockedHint, which exists to say "the
  // page is not served over HTTPS, so the browser blocked the request".
  // Reading the body alone turned all of that into "0 — Request failed".
  if (e.message && e.message !== `API ${e.status}`) return e.message;

  return `${e.status} — ${fallback}`;
}

/**
 * Is this a sentence for a person, or a symbol for a program?
 *
 * EnvelopeMiddleware computes `message = detail.get("message") or raw_code`
 * (app/middleware/envelope.py:283), so a handler that raises
 * detail={"code": "MERGE_NO_NEIGHBOR"} with no prose — as every raise in
 * segment_merge.py and segment_split.py does — yields error.message ==
 * "MERGE_NO_NEIGHBOR". Surfacing that verbatim tells an author
 * "SPLIT_MERGE_BUSY" in a toast. The code is still available to callers at
 * error.details.code; what a person reads should be the caller's sentence.
 */
function isProse(message: string | undefined): boolean {
  return !!message && !/^[A-Z][A-Z0-9_]*$/.test(message);
}
